import {
    Box,
    Button,
    CircularProgress,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Toolbar,
    Typography
} from "@mui/material";
import { FileDownload } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useThemeContext } from "../../../core/contexts/ThemeContext";
import { useMoeda } from "../../../core/hooks/useMoeda";
import { useContas } from "../../hooks/useContas";
import { IContaMensalRelatorio } from "../../interfaces/IContaMensalRelatorio";

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

export const RelatorioContasMensais = () => {
    const { theme } = useThemeContext();
    const { real } = useMoeda();
    const { buscarContasMensais, exportarContasMensais, loading } = useContas();

    const [ano, setAno] = useState(new Date().getFullYear());
    const [tipo, setTipo] = useState('');
    const [pago, setPago] = useState('');
    const [dados, setDados] = useState<IContaMensalRelatorio[]>([]);

    const buscar = async () => {
        const pagoParam = pago === '' ? undefined : pago === 'true';
        const tipoParam = tipo || undefined;
        const resultado = await buscarContasMensais(ano, tipoParam, pagoParam);
        if (resultado) setDados(resultado);
    };

    useEffect(() => {
        buscar();
    }, []);

    const dadosGrafico = dados.map((item) => ({
        mes: MESES[item.mes - 1],
        pagar: item.totalPagar,
        receber: item.totalReceber,
    }));

    const CustomToolTip = (props: any) => {
        const { active, payload } = props;
        if (!active || !payload?.length) return null;
        return (
            <Box sx={{ background: 'white', border: '1px solid #ddd', padding: '8px', borderRadius: '4px' }}>
                {payload.map((item: any, i: number) => (
                    <Typography key={i} variant="subtitle2" fontWeight="bold" color={item.fill}>
                        {item.name}: {real(item.value)}
                    </Typography>
                ))}
            </Box>
        );
    };

    return (
        <div style={{ maxWidth: '95%', margin: '0 auto', paddingTop: '1rem' }}>
            <Paper sx={{ padding: '1rem', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <TextField
                        label="Ano"
                        type="number"
                        value={ano}
                        onChange={(e) => setAno(parseInt(e.target.value))}
                        size="small"
                        sx={{ width: 120 }}
                        inputProps={{ min: 2000, max: 2100 }}
                    />
                    <Select
                        value={tipo}
                        onChange={(e) => setTipo(e.target.value)}
                        displayEmpty
                        size="small"
                        sx={{ width: 180 }}
                    >
                        <MenuItem value="">Todos os tipos</MenuItem>
                        <MenuItem value="pagar">A Pagar</MenuItem>
                        <MenuItem value="receber">A Receber</MenuItem>
                    </Select>
                    <Select
                        value={pago}
                        onChange={(e) => setPago(e.target.value)}
                        displayEmpty
                        size="small"
                        sx={{ width: 180 }}
                    >
                        <MenuItem value="">Todos os status</MenuItem>
                        <MenuItem value="false">Pendente</MenuItem>
                        <MenuItem value="true">Pago</MenuItem>
                    </Select>
                    <Button variant="contained" size="small" onClick={buscar} disabled={loading}>
                        {loading ? <CircularProgress size={18} /> : 'Filtrar'}
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={loading ? <CircularProgress size={14} /> : <FileDownload />}
                        onClick={() => exportarContasMensais(ano, tipo || undefined, pago === '' ? undefined : pago === 'true')}
                        disabled={loading}
                    >
                        Exportar
                    </Button>
                </div>
            </Paper>

            <Paper sx={{ padding: '1rem', height: '400px', marginBottom: '1rem' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Contas por Mês — {ano}
                </Typography>
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80%' }}>
                        <CircularProgress />
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height="90%">
                        <BarChart data={dadosGrafico} margin={{ top: 5, right: 40, left: 60, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="mes" angle={-45} height={60} interval={0} tick={{ fontSize: 11 }} />
                            <YAxis tickFormatter={(valor: string) => real(valor)} />
                            <Tooltip content={CustomToolTip} />
                            <Legend />
                            <Bar dataKey="pagar" name="A Pagar" fill={theme.palette.error.main} />
                            <Bar dataKey="receber" name="A Receber" fill={theme.palette.success.main} />
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </Paper>

            <Paper>
                <TableContainer>
                    <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 } }}>
                        <Typography variant="h6" fontWeight="800">
                            Resumo Mensal
                        </Typography>
                    </Toolbar>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Mês</TableCell>
                                <TableCell>Total a Pagar</TableCell>
                                <TableCell>Total a Receber</TableCell>
                                <TableCell>Qtd. Contas</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {dados.length ? (
                                dados.map((item) => (
                                    <TableRow key={item.mes}>
                                        <TableCell>{MESES[item.mes - 1]}</TableCell>
                                        <TableCell>{real(item.totalPagar)}</TableCell>
                                        <TableCell>{real(item.totalReceber)}</TableCell>
                                        <TableCell>{item.quantidadeContas}</TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        Nenhum dado encontrado para o período
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </div>
    );
};
